import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Pill, SectionHead, PrimaryLink, CTABand, SpecDisclaimer, NetworkNote } from "../components/bits";
import DecisionSelector from "../components/DecisionSelector";
import { COMPARE_ROWS } from "../content/pages";
import { PRODUCTS } from "../content/products";

export default function Compare() {
  return (
    <>
      <Seo
        path="/compare-smart-labels"
        title="Compare GNTS1 vs LL100 Smart Labels"
        description="Outcome-led comparison of the GNTS1 Bluetooth smart sticker and the LL100 4G cellular smart label — coverage model, cost profile, battery, air freight and typical fit."
      />
      <section className="border-b border-border bg-brand-surface">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <h1 className="max-w-3xl text-4xl font-semibold tracking-tight text-brand-navy sm:text-5xl">
            Bluetooth sticker vs 4G label — an honest comparison
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-slate md:text-lg">
            No "winner". These are different tools for different lanes. Read the rows as outcomes you'd want to achieve,
            and note which column describes your operation.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Pill label={PRODUCTS.gnts1.pill.label} tone="cyan" />
            <Pill label={PRODUCTS.ll100.pill.label} tone="lime" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8" data-testid="compare-table-section">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse border border-border text-sm" data-testid="compare-table">
            <thead>
              <tr className="bg-brand-surface">
                <th scope="col" className="w-1/4 border border-border p-4 text-left font-mono text-xs uppercase tracking-wider text-brand-slate">Outcome you want</th>
                <th scope="col" className="border border-border p-4 text-left">
                  <Pill label="Bluetooth" tone="cyan" />
                  <span className="mt-2 block font-heading text-base font-semibold text-brand-navy">{PRODUCTS.gnts1.fullName}</span>
                </th>
                <th scope="col" className="border border-border p-4 text-left">
                  <Pill label="4G Cellular + GNSS" tone="lime" />
                  <span className="mt-2 block font-heading text-base font-semibold text-brand-navy">{PRODUCTS.ll100.fullName}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((r) => (
                <tr key={r.outcome} className="align-top transition-colors duration-150 hover:bg-brand-surface/60">
                  <th scope="row" className="border border-border p-4 text-left font-semibold text-brand-navy">{r.outcome}</th>
                  <td className="border border-border p-4 leading-relaxed text-brand-slate">{r.g}</td>
                  <td className="border border-border p-4 leading-relaxed text-brand-slate">{r.l}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <NetworkNote />
          <SpecDisclaimer />
          <div className="rounded-sm border border-border bg-brand-surface p-4 text-sm leading-relaxed text-brand-slate">
            <strong className="text-brand-navy">On pricing:</strong> we don't publish prices we can't stand behind.
            Per-unit and connectivity pricing is shared during scoping, against your actual volumes.
            [Published pricing pending approval.]
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-brand-surface" data-testid="compare-selector">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <SectionHead
            kicker="Still torn?"
            title="Answer four questions for a likely fit"
            sub="A starting hypothesis, always followed by validation with a human."
          />
          <div className="mt-10"><DecisionSelector compact /></div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <SectionHead kicker="Deep dives" title="Read the full product pages" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {Object.values(PRODUCTS).map((p) => (
            <Link key={p.id} to={p.slug} data-testid={`compare-deepdive-${p.id}`}
              className="rounded-sm border border-border p-6 transition-colors duration-150 hover:border-primary/50">
              <Pill label={p.pill.label} tone={p.pill.tone} />
              <h3 className="mt-3 text-lg font-semibold text-brand-navy">{p.fullName}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-slate">{p.proposition}</p>
            </Link>
          ))}
        </div>
        <div className="mt-12"><PrimaryLink to="/request-a-pilot" testid="compare-cta-pilot">Discuss Your Shipment</PrimaryLink></div>
      </section>

      <CTABand title="A mixed estate is a legitimate answer" sub="Many customers run GNTS1 on the volume lanes and LL100 on the shipments that roam. A pilot finds your split with real data." />
    </>
  );
}
